/* import __COLOCATED_TEMPLATE__ from './qualification-rates.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { useResource } from 'ember-resources';
import ChartDataResourceCompatible from 'embercom/lib/reporting/chart-data-resource-compatible';
import { buildDataConfigWithComparisons } from 'embercom/lib/reporting/comparison-data-config';
import { tracked } from '@glimmer/tracking';

function getValueFromChartData(rawChartData: $TSFixMe): number {
  return rawChartData.aggregations[0]?.values[0] ?? 0;
}

interface Signature {
  Args: {
    dataConfig: $TSFixMe;
    viewConfig: $TSFixMe;
    range: $TSFixMe;
    renderableChart: $TSFixMe;
    openUnderlyingDataModal: () => void;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class QualificationRates extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked error: string | null = null;

  dataResource = useResource(this, ChartDataResourceCompatible, () => ({
    dataConfig: buildDataConfigWithComparisons(
      this.args.dataConfig,
      this.args.renderableChart.dateRange,
    ),
    viewConfig: this.args.viewConfig,
    onError: (error: $TSFixMe) => {
      this.error = error.jqXHR?.responseJSON?.error;
    },
    onSuccess: () => {
      this.error = null;
    },
  }));

  get isLoading() {
    return this.dataResource.isLoading;
  }

  get showErrorBanner() {
    return this.error && !this.dataResource.isLoading;
  }

  get noRawChartData() {
    return !this.dataResource.rawChartData?.length;
  }

  get rawValues(): number[] {
    return this.dataResource.rawChartData.map(getValueFromChartData);
  }

  // buildDataConfigWithComparisons ordering: [prev_outcome, prev_total, curr_outcome, curr_total]
  get currentRate(): number | null {
    let values = this.rawValues;
    if (values.length < 4) {
      return null;
    }
    let currOutcome = values[2];
    let currTotal = values[3];
    return currTotal > 0 ? (currOutcome / currTotal) * 100 : null;
  }

  get previousRate(): number | null {
    let values = this.rawValues;
    if (values.length < 4) {
      return null;
    }
    let prevOutcome = values[0];
    let prevTotal = values[1];
    return prevTotal > 0 ? (prevOutcome / prevTotal) * 100 : null;
  }

  get hasData(): boolean {
    return this.currentRate !== null;
  }

  get formattedRate(): string {
    if (this.currentRate === null) {
      return '\u2014';
    }
    return this.intl.formatNumber(this.currentRate / 100, {
      style: 'percent',
      maximumFractionDigits: 2,
    });
  }

  get showSummaryComparison() {
    return this.currentRate !== null && this.previousRate !== null;
  }

  get timeRangeData() {
    return {
      inDays: this.args.range.inDays || 0,
      comparisonStart: this.args.range.comparisonStartMoment,
      comparisonEnd: this.args.range.comparisonEndMoment,
      timezone: this.args.range.timezone,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::SdrLeads::QualificationRates': typeof QualificationRates;
    'reporting/bespoke/sdr-leads/qualification-rates': typeof QualificationRates;
  }
}
